import { useEffect, useState } from 'react'

/**
 * A clock for the page, not for every component.
 *
 * Countdowns and "2 days overdue" only need to be right to the minute, and a
 * per-second render of a table is a lot of work for a label nobody reads twice
 * in a second. Pass a coarser interval where minutes are enough.
 */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(timer)
  }, [intervalMs])

  return now
}

/** Wake this long after a boundary so a rule that flips at it sees it passed. */
const BOUNDARY_GUARD_MS = 250

/**
 * A clock that also wakes at the instants the caller's rules change.
 *
 * `useNow` alone ticks on a fixed interval, so an action that appears or
 * disappears at a meeting's start or end could lag most of a minute behind —
 * long enough to still offer Brief on a meeting that has just finished. Pass
 * those transitions as `boundaries` (milliseconds on the same clock) and the
 * next render is scheduled for the nearest one still ahead; `intervalMs` stays
 * the ceiling, so relative labels elsewhere on the page keep refreshing.
 */
export function useNowAtBoundaries(
  boundaries: readonly number[],
  intervalMs = 60_000
): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const current = Date.now()
    let next: number | null = null
    for (const at of boundaries) {
      if (!Number.isFinite(at) || at <= current) continue
      if (next === null || at < next) next = at
    }
    const delay =
      next === null
        ? intervalMs
        : Math.min(Math.max(next - current + BOUNDARY_GUARD_MS, BOUNDARY_GUARD_MS), intervalMs)
    const timer = window.setTimeout(() => setNow(Date.now()), delay)
    return () => window.clearTimeout(timer)
    // `now` is a dependency so every tick reschedules against the next boundary.
  }, [boundaries, intervalMs, now])

  return now
}
